import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import * as Yup from "yup";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
// form
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// @mui
import { LoadingButton } from "@mui/lab";
import { Box, Card, Grid, Stack, Typography } from "@mui/material";
// utils
// routes
import { PATH_DASHBOARD } from "../../../routes/paths";

// components
import { useSnackbar } from "../../../components/snackbar";

import { CustomAvatar } from "../../../components/custom-avatar";
import FormProvider, { RHFTextField } from "../../../components/hook-form";
import { updateUser } from "../../../redux/slices/user";

// ----------------------------------------------------------------------

UserEditForm.propTypes = {
  isEdit: PropTypes.bool,
  currentUser: PropTypes.object,
  logout: PropTypes.func,
};

export default function UserEditForm({ isEdit = false, currentUser, logout }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { enqueueSnackbar } = useSnackbar();

  const NewUserSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    email: Yup.string()
      .required("Email is required")
      .email("Email must be a valid email address"),
    phoneNumber: Yup.string().required("Phone is required"),
    // .length(10, "Phone number must be exactly 10 digits"), // address: Yup.string().required('Address is required'),
    // role: Yup.string().required('Role is required'),
    // avatarUrl: Yup.mixed().required('Avatar is required'),
  });

  const defaultValues = useMemo(
    () => ({
      name: currentUser?.name || "",
      email: currentUser?.email || "",
      phoneNumber: currentUser?.phone || "",
      address: currentUser?.address || "",
      avatarUrl: currentUser?.avatarUrl || null,
      isVerified: currentUser?.phone_verified || true,
      status: currentUser?.status,
      role: currentUser?.roles || "",
      detail: currentUser?.detail,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentUser]
  );
  const methods = useForm({
    resolver: yupResolver(NewUserSchema),
    defaultValues,
  });

  const {
    reset,
    watch,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const values = watch();

  useEffect(() => {
    if (isEdit && currentUser) {
      reset(defaultValues);
    }
    if (!isEdit) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, currentUser]);

  const onSubmit = async (data) => {
    try {
      // await new Promise((resolve) => setTimeout(resolve, 500));
      dispatch(updateUser(currentUser?.id, data));
      reset();
      enqueueSnackbar(!isEdit ? "Create success!" : "Update success!");
      navigate(PATH_DASHBOARD.user.list);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        {isEdit && (
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 3 }}>
              <CustomAvatar
                src={currentUser?.photoURL}
                alt={currentUser?.name}
                name={currentUser?.name}
                sx={{
                  mx: "auto",
                  borderWidth: 2,
                  borderStyle: "solid",
                  borderColor: "common.white",
                  width: { xs: 80, md: 128 },
                  height: { xs: 80, md: 128 },
                }}
              />
              <Stack>
                <Typography sx={{ textAlign: "center" }} variant="h4">
                  {currentUser?.name}
                </Typography>

                <Typography sx={{ opacity: 0.72, textAlign: "center" }}>
                  {currentUser?.email}
                </Typography>
                <Typography sx={{ opacity: 0.72, textAlign: "center" }}>
                  {currentUser?.phone}
                </Typography>

                {/* <Label
                  color={values.status === 1 ? "success" : "error"}
                  sx={{
                    textTransform: "uppercase",
                    position: "absolute",
                    top: 24,
                    right: 24,
                  }}
                >
                  {values.status === 1 ? "Active" : "Inactive"}
                </Label> */}
              </Stack>
              {/* <Stack sx={{ marginTop: 4, cursor: 'pointer' }} direction="row" onClick={logout}>
                <StyledIcon icon="eva:log-out-outline" />
                <Typography variant="body2">Logout</Typography>
              </Stack> */}
            </Card>
          </Grid>
        )}

        <Grid item xs={12} md={isEdit ? 8 : 12}>
          <Card sx={{ p: 3 }}>
            <Box
              rowGap={3}
              columnGap={2}
              display="grid"
              gridTemplateColumns={{
                xs: "repeat(1, 1fr)",
                sm: "repeat(2, 1fr)",
              }}
            >
              <RHFTextField name="name" label="Full Name" />
              <RHFTextField name="email" label="Email Address" />
              <RHFTextField
                type="number"
                name="phoneNumber"
                label="Phone Number"
              />
            </Box>

            <Stack alignItems="flex-end" sx={{ mt: 3 }}>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={isSubmitting}
              >
                {!isEdit ? "Create User" : "Save Changes"}
              </LoadingButton>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
}
